import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Tooltip } from '@fiverr-private/fit';
import ResizeObserver from 'resize-observer-polyfill';
import Thumbnail from '../Thumbnail';
import InvitePopUpManager from '../InvitePopUpManager';
import { downloadLink, RenderIf } from '../../../Utils';
import * as CurrentsActions from '../../actions/current';
import * as CollaboratorsSelectors from '../../selectors/collaborators';
import * as CurrentsSelectors from '../../selectors/current';
import * as FilesSelectors from '../../selectors/files';
import * as FilerrsSelectors from '../../selectors/filerrs';
import * as Translations from '../../providers/i18n';
import ArrowIcon from './icons/ArrowIcon';

import './style.scss';

const JUMP_STEPS_IN_PX = 500;

class Thumbnails extends Component {

    constructor(props) {
        super(props);

        this.state = {
            translate: 0
        };
    }

    componentDidMount() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.thumbWrapper);

        this.handleCurrentFilerrIdUpdate({});
    }

    componentDidUpdate(prevProps) {
        this.handleCurrentFilerrIdUpdate(prevProps);
    }

    componentWillUnmount() {
        this.resizeObserver.disconnect();
    }

    /**
     * onResize function will run on each window resize.
     * When resizing the window we want to make sure that the current translate does fit the new window size.
     * This updateTranslate function will make sure to normalize the translate (if needed) so we don't overflow from the slider.
     */
    onResize = () => {
        this.updateTranslate(this.state.translate);
    }

    /**
     * This function will make sure that the current selected file index is visible in the slider.
     * Once the file thumbnail is visible then don't do anything. Else, slide the thumbnails to show the current file thumbnail.
     */
    handleCurrentFilerrIdUpdate = (prevProps) => {
        const { filerrIds, currentFilerrId } = this.props;
        const { currentFilerrId: prevCurrentFilerrId } = prevProps;

        if (currentFilerrId !== prevCurrentFilerrId) {
            const currentIndex = filerrIds.indexOf(currentFilerrId);

            if (!this.isThumbnailVisible(currentIndex)) {
                this.updateTranslate(currentIndex * 100);
            }
        }
    }

    isThumbnailVisible = (index) => {
        const itemClassName = this.getThumbnailItemSelectorClassName(index);
        const thumbnailItem = this.thumbWrapper.querySelector(`.${itemClassName}`);
        if (!thumbnailItem) { return; }

        const { left: wrapperLeft, right: wrapperRight } = this.thumbWrapper.getBoundingClientRect();
        const { left, right } = thumbnailItem.getBoundingClientRect();
        if (left - wrapperLeft >= 50 && right - wrapperRight <= -36) {
            return true;
        }

        return false;
    }

    /**
     * updateTranslate function will make sure to normalize the translation so it don't overflow from the left and the right.
     * If the translation we get is below 0 then we reset it to 0 in order to prevent an overflow from the left, If the translation
     * is bigger than it should be then we normalize it to the maximum depends on the slider and the wrapper widths in order to prevent an overflow from the right.
     * This function also get called for any resizes going to the thumbnails wrapper including zooming in/out and toggling the comments panel.
     */
    updateTranslate = (newTranslate) => {
        const { width: sliderWidth } = this.thumbSlider.getBoundingClientRect();
        const { width: wrapperWidth } = this.thumbWrapper.getBoundingClientRect();

        const overflowRight = newTranslate + wrapperWidth >= sliderWidth;

        let normalizedTranslation = newTranslate;
        if (newTranslate <= 0) {
            normalizedTranslation = 0;
        } else if (overflowRight) {
            normalizedTranslation = sliderWidth - wrapperWidth + 50;
        }

        this.setState({
            translate: normalizedTranslation,
            disableLeft: normalizedTranslation === 0,
            disableRight: overflowRight,
            showArrows: sliderWidth > wrapperWidth
        });
    }

    calculateTransform = () => {
        const { translate } = this.state;
        return `translateX(-${translate}px)`;
    }

    handleThumbnailClick = (filerrId) => {
        this.props.onThumbnailClick(filerrId);
    }

    handleLeftClick = () => {
        const { translate } = this.state;
        this.updateTranslate(translate - JUMP_STEPS_IN_PX);
    }

    handleRightClick = () => {
        const { translate } = this.state;
        this.updateTranslate(translate + JUMP_STEPS_IN_PX);
    }

    handleDownloadClick = () => {
        const {
            currentFilerrDownloadUrl,
            currentFilerrPreviewType
        } = this.props;

        downloadLink(
            currentFilerrDownloadUrl,
            'Viewer Carousel',
            currentFilerrPreviewType
        );
    };

    getButtonsOnThumbnail = (i18n) => {
        const Trigger = ({ onClick }) => (
            <Tooltip content={i18n.t('attachments_preview.invite.share_with_others')}>
                <div onClick={onClick}
                    className="icn-share"/>
            </Tooltip>
        );

        return (
            <ul className="btns-group">
                <li>
                    <Tooltip content={i18n.t('attachments_preview.download')}>
                        <div onClick={this.handleDownloadClick} className="icn-download" data-download-link="" tabIndex={0}/>
                    </Tooltip>
                </li>
                {
                    this.props.currentCollaboratorCanInvite &&
                    (
                        <li>
                            <InvitePopUpManager onlyCurrentFile={true}
                                trigger={Trigger}/>
                        </li>
                    )
                }
            </ul>
        );
    };

    getThumbnailItemSelectorClassName = (index) =>
        `thumb-${index}`;

    getThumbnailItemClassNames = (filerrId, index) => {
        const {
            currentFilerrId
        } = this.props;

        return classNames(
            this.getThumbnailItemSelectorClassName(index),
            { selected: filerrId === currentFilerrId }
        );
    }

    render() {
        const {
            filerrIds,
            i18n
        } = this.props;

        const {
            showArrows,
            disableLeft,
            disableRight
        } = this.state;

        return (
            <ul className={classNames('thumbnails-wrapper', { hidden: filerrIds.length <= 1 })} ref={(ref) => this.thumbWrapper = ref}>
                <RenderIf condition={showArrows}>
                    <ArrowIcon className={classNames('nav-arrow left', { disabled: disableLeft })} onClick={this.handleLeftClick}/>
                    <ArrowIcon className={classNames('nav-arrow right', { disabled: disableRight })} onClick={this.handleRightClick}/>
                </RenderIf>
                <div className="thumbs-slider" ref={(ref) => this.thumbSlider = ref} style={{ transform: this.calculateTransform() }}>
                    {filerrIds.map((filerrId, index) => (
                        <li key={index}
                            className={this.getThumbnailItemClassNames(filerrId, index)}
                            onClick={() => this.handleThumbnailClick(filerrId, index)}>
                            <Thumbnail key={filerrId} filerrId={filerrId}>
                                { this.getButtonsOnThumbnail(i18n) }
                            </Thumbnail>
                        </li>
                    ))}
                </div>
            </ul>
        );
    }
}

Thumbnails.propTypes = {
    filerrIds: PropTypes.array,
    currentFilerrId: PropTypes.string,
    currentFilerrDownloadUrl: PropTypes.string,
    currentFilerrPreviewType: PropTypes.string,
    currentCollaboratorCanInvite: PropTypes.bool,
    onThumbnailClick: PropTypes.func,
    i18n: PropTypes.shape({
        t: PropTypes.func
    })
};

Thumbnails.defaultProps = {
    filerrIds: [],
    onThumbnailClick: () => true
};

const mapStateToProps = (state) => ({
    filerrIds: FilerrsSelectors.getFilerrIds(state),
    currentFilerrId: CurrentsSelectors.getCurrentFilerrId(state),
    currentFilerrDownloadUrl: FilerrsSelectors.getCurrentFilerrDownloadUrl(state),
    currentFilerrPreviewType: FilerrsSelectors.getCurrentFilerrPreviewType(state),
    currentCollaboratorCanInvite: CollaboratorsSelectors.getCurrentCollaboratorInvitePermissions(state)
});

const mapDispatchToProps = (dispatch) => {
    const setCurrents = (filerrId) => (dispatch, getState) => {
        dispatch(CurrentsActions.setCurrentFilerrId(filerrId));
        dispatch(CurrentsActions.setCurrentFileId(FilesSelectors.getFileIdByFileFilerrId(getState(), filerrId)));
        dispatch(CurrentsActions.setCurrentCommentId());
        dispatch(CurrentsActions.setCurrentTimecode());
    };

    return {
        onThumbnailClick: (filerrId) => dispatch(setCurrents(filerrId))
    };
};

export default compose(
    Translations.connect,
    connect(mapStateToProps, mapDispatchToProps))(Thumbnails);
